import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { CustomOptions } from '@/types/axios'
import { ElMessage } from 'element-plus'
import router from '@/router/index'
import store from '@/store'
import { cloneDeep } from '@/utils/index'
import { getLocalItem } from '@/utils/localStorage'

// 正在进行请求的接口信息
const pendingMap = new Map()

const LoadingInstance = {
  count: 0
}

const minLoadingDuration = 400 // 最小延迟时间，单位：毫秒
let loadingTimeout: any

/**
 * 显示全局loading
 * @param {*} config
 */
export const showLoading = (config: AxiosRequestConfig) => {
  // 判断是否展示全局loading
  if (config?._loading && config._loading === true) {
    delayLoading(config)
  }
}

/**
 * 关闭Loading层实例
 * @param {*} config
 */
export const closeLoading = (config: { _loading?: boolean }) => {
  if (config?._loading) cancelDelayedLoading()
  if (config?._loading && LoadingInstance.count > 0) LoadingInstance.count--
  if (LoadingInstance.count <= 0) {
    store.commit('moduleCommon/HTTP_LOADING', false)
  }
}

/**
 * 延迟请求loading展示
 * @param response
 */
export const delayLoading = (config: AxiosRequestConfig) => {
  // 默认get请求延迟loading展示
  if (config.method === 'get' || config?._minLoadingDuration) {
    if (loadingTimeout) cancelDelayedLoading()
    loadingTimeout = setTimeout(() => {
      LoadingInstance.count++
      store.commit('moduleCommon/HTTP_LOADING', true)
    }, config?._minLoadingDuration || minLoadingDuration)
  } else {
    LoadingInstance.count++
    store.commit('moduleCommon/HTTP_LOADING', true)
  }
}

export const cancelDelayedLoading = () => {
  loadingTimeout && clearTimeout(loadingTimeout)
}

// 请求完结判断
export const editResponse = (response: AxiosResponse) => {
  const code = response?.data?.code
  closeLoading({ _loading: response.config._loading })

  // 判断当前获取的状态
  if (code !== 0) {
    // 未登录跳转到登录页
    if (code === 4002) {
      // 在登录页面无需提示
      if (router.currentRoute.value.name !== 'login') {
        // 判断是否渠道
        if (getLocalItem('zl-mctcode') && getLocalItem('zl-channel-backUrl')) {
          window.open(getLocalItem('zl-channel-backUrl'), '_self')
        } else {
          store.dispatch('moduleCommon/logout')
          ElMessage.closeAll()
          ElMessage({
            message: '长时间未操作，请重新登录！',
            type: 'warning'
          })
        }
      }
      return
    }
    if (response.config?._errorMessageShow && response?.data?.message) {
      // 手机号已注册弹框提示
      if (code !== 2002 && !response.config?._hideMsg) {
        if (response.config?._confirmMsg) {
          // 错误信息需用户确认
          store.commit('moduleCommon/CONFIEM_ERR_MSG_OBJ', {
            show: true,
            content: response.data.message
          })
        } else {
          ElMessage.error(response.data.message)
        }
      }
    }
  }
}

/**
 * 储存每个请求的唯一cancel回调, 以此为标识
 * @param {*} config
 */
export const addPending = (config: AxiosRequestConfig) => {
  const { _repeatInputCancel } = config
  if (!_repeatInputCancel) {
    return
  }
  const pendingKey = getPendingKey(config)
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pendingMap.has(pendingKey)) {
        pendingMap.set(pendingKey, cancel)
      }
    })
}

/**
 * 删除重复的请求
 * @param {*} config
 */
export const removePending = (config: AxiosRequestConfig) => {
  const { _repeatInputCancel } = config
  if (!_repeatInputCancel) {
    return
  }
  const pendingKey = getPendingKey(config)
  if (pendingMap.has(pendingKey)) {
    const cancelToken = pendingMap.get(pendingKey)
    cancelToken(pendingKey)
    pendingMap.delete(pendingKey)
  }
}

/**
 * 生成唯一的每个请求的唯一key
 * @param {*} config
 * @returns
 */
export const getPendingKey = (config: AxiosRequestConfig) => {
  let { url, method, params, data, _repeatInputCancel } = config
  if (typeof data === 'string') data = JSON.parse(data)
  if (_repeatInputCancel) {
    return [url, method].join('&')
  } else {
    return [url, method, JSON.stringify(params), JSON.stringify(data)].join('&')
  }
}

/**
 * 处理额外的参数信息
 */
export const filterConfig = (customConfig: CustomOptions, config?: AxiosRequestConfig) => {
  return Object.assign(cloneDeep(customConfig), config)
}

/**
 * 处理异常
 * @param {*} error
 */
export function httpErrorStatusHandle(error: {
  message: string | string[]
  response: { status: any; config: { url: any } }
}) {
  // 处理被取消的请求
  if (axios.isCancel(error)) return console.error('请求的重复请求：' + error.message)
  let message = ''
  if (error && error.response) {
    switch (error.response.status) {
      case 302:
        message = '接口重定向了！'
        break
      case 400:
        message = '参数不正确！'
        break
      case 401:
        message = '您未登录，或者登录已经超时，请先登录！'
        store.dispatch('moduleCommon/logout')
        break
      case 403:
        message = '您没有权限操作！'
        break
      case 404:
        message = `请求地址出错: ${error.response.config.url}`
        break // 在正确域名下
      case 408:
        message = '请求超时！'
        break
      case 409:
        message = '系统已存在相同数据！'
        break
      case 500:
        message = '服务器内部错误！'
        break
      case 501:
        message = '服务未实现！'
        break
      case 502:
        message = '网关错误！'
        break
      case 503:
        message = '服务不可用！'
        break
      case 504:
        message = '服务暂时无法访问，请稍后再试！'
        break
      case 505:
        message = 'HTTP版本不受支持！'
        break
      default:
        message = '异常问题，请联系管理员！'
        break
    }
  }
  if (error.message.includes('timeout')) message = '网络请求超时！'
  if (error.message.includes('Network')) message = window.navigator.onLine ? '服务端异常！' : '您断网了！'

  ElMessage({
    type: 'warning',
    message
  })
}

/**
 * 请求重试机制
 */
export const axiosRetry = (error: any) => {
  if (!error.config) {
    return errorOption(error, 1)
  }

  const config = error.config
  if (!config || !config?._requestRetry) return errorOption(error, 1)

  config._retryCount = config._retryCount || 0
  config._retryCount += 1

  // 检查重试次数是否已达到最大值
  if (config._retryCount >= 3 || config?._repeatInputCancel) {
    errorOption(error, config?._repeatInputCancel ? 1 : 3)
    return Promise.reject(error)
  }

  const backoff = new Promise<void>(function (resolve) {
    const timer = setTimeout(function () {
      resolve()
      clearTimeout(timer)
    }, 1000)
  })

  return backoff.then(() => {
    return axios(config)
  })
}

export const errorOption = (error: any, count: number) => {
  LoadingInstance.count -= count
  closeLoading({ _loading: true })

  error?.config && removePending(error.config)
  httpErrorStatusHandle(error)
}
